import type { ChecklistTemplate } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';
import { SeasonType } from '@/types/season';

const TEMPLATE_ID = 'gun-season-midwest-v1';

const BASE_DATE = '2026-01-01T00:00:00.000Z';

// Stable IDs for all 37 template tasks — used in dependsOn arrays below
const T = {
  orderSeed: 'tpl-gun-mw-001',
  orderTrees: 'tpl-gun-mw-002',
  postSeasonScouting: 'tpl-gun-mw-003',
  markBedding: 'tpl-gun-mw-004',
  cameraTransition: 'tpl-gun-mw-005',
  budgetPlan: 'tpl-gun-mw-006',
  neighborOutreach: 'tpl-gun-mw-007',
  purchaseChemicals: 'tpl-gun-mw-008',
  soilTesting: 'tpl-gun-mw-009',
  tsiHingeCuts: 'tpl-gun-mw-010',
  shedHunting: 'tpl-gun-mw-011',
  frostSeedClover: 'tpl-gun-mw-012',
  springTreePlanting: 'tpl-gun-mw-013',
  predatorControl: 'tpl-gun-mw-014',
  applyLime: 'tpl-gun-mw-015',
  warmSeasonPlanting: 'tpl-gun-mw-016',
  trailCamSummer: 'tpl-gun-mw-017',
  waterholeMainten: 'tpl-gun-mw-018',
  mockScrapeInstall: 'tpl-gun-mw-019',
  treestandHanging: 'tpl-gun-mw-020',
  lifelines: 'tpl-gun-mw-021',
  shootingLaneTrim: 'tpl-gun-mw-022',
  fallBrassicas: 'tpl-gun-mw-023',
  fallCerealGrains: 'tpl-gun-mw-024',
  cameraCardPull: 'tpl-gun-mw-025',
  bowTuneUp: 'tpl-gun-mw-026',
  fallFertilize: 'tpl-gun-mw-027',
  rifleZero: 'tpl-gun-mw-028',
  licensePurchase: 'tpl-gun-mw-029',
  mineralRefresh: 'tpl-gun-mw-030',
  huntingClothing: 'tpl-gun-mw-031',
  cameraReposition: 'tpl-gun-mw-032',
  finalAccessRoute: 'tpl-gun-mw-033',
  packBags: 'tpl-gun-mw-034',
  postSeasonCameraReview: 'tpl-gun-mw-035',
  postSeasonPlotEval: 'tpl-gun-mw-036',
  equipmentStorage: 'tpl-gun-mw-037',
} as const;

export const DEFAULT_TEMPLATE: ChecklistTemplate = {
  id: TEMPLATE_ID,
  name: 'Whitetail Gun Season Prep — Midwest',
  description:
    'Comprehensive year-round preparation checklist for Midwest whitetail gun season. Covers food plots, stand prep, trail cameras, scouting, gear, habitat management, and post-season review.',
  seasonType: SeasonType.Gun,
  region: 'midwest',
  isBuiltIn: true,
  version: 2,
  createdAt: BASE_DATE,
  updatedAt: BASE_DATE,
  tasks: [
    // ─── January: Post-season admin & planning ────────────────────────────────
    {
      id: T.orderSeed,
      templateId: TEMPLATE_ID,
      title: 'Order food plot seed',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 0,
      notes: 'Order early — shipping lead times. Brassica, clover, rye mixes.',
    },
    {
      id: T.orderTrees,
      templateId: TEMPLATE_ID,
      title: 'Order trees/shrubs for spring planting',
      description: '',
      category: TaskCategory.TreePlanting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['order'],
      sortOrder: 1,
      notes: 'Fruit trees, screening conifers, mast trees. State nurseries often have early deadlines.',
    },
    {
      id: T.postSeasonScouting,
      templateId: TEMPLATE_ID,
      title: 'Post-season scouting: bedding and travel routes',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['post_season'],
      sortOrder: 2,
      notes: 'Scout while sign is fresh. Mark bedding areas, travel corridors. Use drone if available.',
    },
    {
      id: T.markBedding,
      templateId: TEMPLATE_ID,
      title: 'Mark bedding areas and corridors (flagging tape)',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 25,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.postSeasonScouting],
      predefinedActions: [],
      sortOrder: 3,
      notes: 'Flag tape bedding areas and travel corridors found during post-season scouting.',
    },
    {
      id: T.cameraTransition,
      templateId: TEMPLATE_ID,
      title: 'Camera transition: cell → SD for survival data',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['transition'],
      sortOrder: 4,
      notes: 'Turn off cell cams (save battery/cost). Keep SD cams rolling 6 more weeks for survival and late-season movement data.',
    },
    {
      id: T.budgetPlan,
      templateId: TEMPLATE_ID,
      title: 'Budget and plan property improvements',
      description: '',
      category: TaskCategory.PropertyInfrastructure,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['plan'],
      sortOrder: 5,
      notes: 'Build and prioritize improvement list. Plan budget for the year.',
    },
    {
      id: T.neighborOutreach,
      templateId: TEMPLATE_ID,
      title: 'Neighbor outreach: connect, promote QDM',
      description: '',
      category: TaskCategory.NeighborAccess,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['contact', 'meet'],
      sortOrder: 6,
      notes: 'Connect with neighbors. Promote QDM. Express interest in their land.',
    },
    {
      id: T.purchaseChemicals,
      templateId: TEMPLATE_ID,
      title: 'Purchase chemicals/glyphosate (early — shortages)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['spray'],
      sortOrder: 7,
      notes: 'Order glyphosate and chemicals early. Supply shortages are common.',
    },
    // ─── Opener-relative: Pre-season planning ─────────────────────────────────
    {
      id: T.soilTesting,
      templateId: TEMPLATE_ID,
      title: 'Soil testing: food plot areas',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -150,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['soil_test'],
      sortOrder: 8,
      notes: 'Soil test results drive fertilizer and lime decisions. Allow 2-3 weeks for lab results.',
    },
    // ─── February: Habitat work ───────────────────────────────────────────────
    {
      id: T.tsiHingeCuts,
      templateId: TEMPLATE_ID,
      title: 'TSI / hinge cuts / bedding area cuts',
      description: '',
      category: TaskCategory.HabitatManagement,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['cut'],
      sortOrder: 9,
      notes: 'Execute TSI, hinge cuts, bedding area creation, corridor work. Plan around snowfalls. Feb-Mar window.',
    },
    {
      id: T.shedHunting,
      templateId: TEMPLATE_ID,
      title: 'Shed hunting',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['shed_hunt'],
      sortOrder: 10,
      notes: 'Feb-Mar. Collect sheds for herd inventory. Note where bucks survived.',
    },
    {
      id: T.frostSeedClover,
      templateId: TEMPLATE_ID,
      title: 'Frost-seed clover (soil temp 32-40°F)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 28,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['frost_seed'],
      sortOrder: 11,
      notes: 'Broadcast clover seed onto frozen/thawing ground. Freeze-thaw cycle works seed into soil. Late Feb - early Mar in Zone 8.',
    },
    // ─── March: Spring planting and predator control ───────────────────────────
    {
      id: T.predatorControl,
      templateId: TEMPLATE_ID,
      title: 'Predator control (pre-fawn drop)',
      description: '',
      category: TaskCategory.PredatorControl,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 3,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['trap', 'hunt'],
      sortOrder: 12,
      notes: 'Mar-May window. Target coyotes and raccoons before fawn drop (late May/June).',
    },
    {
      id: T.springTreePlanting,
      templateId: TEMPLATE_ID,
      title: 'Spring tree planting',
      description: '',
      category: TaskCategory.TreePlanting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 3,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 30,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.orderTrees],
      predefinedActions: ['plant'],
      sortOrder: 13,
      notes: 'Mid-Mar to mid-Apr. Plant fruit trees, screening conifers, mast trees ordered in January.',
    },
    // ─── April: Soil amendment ────────────────────────────────────────────────
    {
      id: T.applyLime,
      templateId: TEMPLATE_ID,
      title: 'Apply lime if soil test requires (6+ wks before planting)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 4,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 30,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.soilTesting],
      predefinedActions: ['fertilize'],
      sortOrder: 14,
      notes: 'Lime needs 6+ weeks to adjust pH before planting. Apply in spring based on soil test results.',
    },
    // ─── May: Warm-season planting ────────────────────────────────────────────
    {
      id: T.warmSeasonPlanting,
      templateId: TEMPLATE_ID,
      title: 'Warm-season food plot planting (soybeans, buckwheat)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 5,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.soilTesting, T.applyLime],
      predefinedActions: ['till', 'plant'],
      sortOrder: 15,
      notes: 'Plant when soil temp reaches 60-65°F. Soybeans, buckwheat, lab lab as summer attraction.',
    },
    // ─── Opener-relative: -90 to -75 days ────────────────────────────────────
    {
      id: T.trailCamSummer,
      templateId: TEMPLATE_ID,
      title: 'Trail cam summer deployment: velvet inventory',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['deploy'],
      sortOrder: 16,
      notes: 'Fresh batteries, new cards. Summer patterns over food plots for velvet buck inventory.',
    },
    {
      id: T.waterholeMainten,
      templateId: TEMPLATE_ID,
      title: 'Waterhole maintenance / creation',
      description: '',
      category: TaskCategory.WaterholeManagement,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['create', 'maintain'],
      sortOrder: 17,
      notes: 'Dig, clean, or refresh waterholes for early-season attraction. Allow time for deer to find and pattern on them.',
    },
    {
      id: T.mockScrapeInstall,
      templateId: TEMPLATE_ID,
      title: 'Mock scrape installation',
      description: '',
      category: TaskCategory.MockScrapes,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['create'],
      sortOrder: 18,
      notes: 'Install mock scrapes with licking branches. Place near travel corridors. Allow 60+ days for bucks to adopt.',
    },
    {
      id: T.treestandHanging,
      templateId: TEMPLATE_ID,
      title: 'Treestand hanging / annual safety check',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['hang', 'inspect'],
      sortOrder: 19,
      notes: 'Hang new stands, inspect existing. Check all straps, bolts, chains, cables. Replace worn safety harness components. -90 to -60 days.',
    },
    {
      id: T.lifelines,
      templateId: TEMPLATE_ID,
      title: 'Install/inspect lifelines and safety systems',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['install', 'inspect'],
      sortOrder: 20,
      notes: 'Lifeline systems for every stand. Non-negotiable safety item.',
    },
    {
      id: T.shootingLaneTrim,
      templateId: TEMPLATE_ID,
      title: 'Shooting lane trimming',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -75,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.treestandHanging],
      predefinedActions: ['trim_lanes'],
      sortOrder: 21,
      notes: 'Minimal disturbance. Trim only what is necessary. Do this AFTER stands are hung so you know exact shooting angles.',
    },
    // ─── August: Fall food plots ───────────────────────────────────────────────
    {
      id: T.fallBrassicas,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: brassicas (Aug 1-15, Zone 8)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.soilTesting],
      predefinedActions: ['till', 'plant'],
      sortOrder: 22,
      notes: 'Brassica mix planting window for Zone 8 (WI). Adjust +/- 2 weeks by zone. This is the primary late-season food plot.',
    },
    {
      id: T.fallCerealGrains,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: cereal grains (Aug 15 - Sep 1)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['plant'],
      sortOrder: 23,
      notes: 'Winter rye, oats, winter wheat. Provides early-season green browse and late-season cover crop.',
    },
    // ─── Opener-relative: -60 to -55 days ────────────────────────────────────
    {
      id: T.cameraCardPull,
      templateId: TEMPLATE_ID,
      title: 'Trail cam card pull and pattern analysis',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['pull_card', 'analyze'],
      sortOrder: 24,
      notes: 'First major pattern check. Identify target bucks, travel times, preferred food sources.',
    },
    {
      id: T.bowTuneUp,
      templateId: TEMPLATE_ID,
      title: 'Bow tune-up and broadhead sharpening',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['tune', 'sharpen'],
      sortOrder: 25,
      notes: 'Paper tune, walk-back tune. Sharpen or replace broadheads. Practice at hunting distances.',
    },
    {
      id: T.fallFertilize,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot fertilize (top-dress nitrogen)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -55,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.fallBrassicas],
      predefinedActions: ['fertilize'],
      sortOrder: 26,
      notes: 'Urea 46-0-0 top-dress on brassica plots. Drives leaf growth before frost sweetening.',
    },
    // ─── Opener-relative: -45 days ────────────────────────────────────────────
    {
      id: T.rifleZero,
      templateId: TEMPLATE_ID,
      title: 'Rifle / shotgun zero check',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['zero'],
      sortOrder: 27,
      notes: 'Range day. Confirm zero at 100 and 200 yards. Check scope mounts and rings for tightness.',
    },
    {
      id: T.licensePurchase,
      templateId: TEMPLATE_ID,
      title: 'License and tag purchase',
      description: '',
      category: TaskCategory.RegulatoryAdmin,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['purchase'],
      sortOrder: 28,
      notes: 'Buy license, tags, and any required permits. Check for regulation changes.',
    },
    {
      id: T.mineralRefresh,
      templateId: TEMPLATE_ID,
      title: 'Mineral site refresh',
      description: '',
      category: TaskCategory.MineralNutrition,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['refresh'],
      sortOrder: 29,
      notes: 'Refresh mineral sites. Note: check state regulations — mineral/bait laws vary by state and season.',
    },
    // ─── Opener-relative: -30 days ────────────────────────────────────────────
    {
      id: T.huntingClothing,
      templateId: TEMPLATE_ID,
      title: 'Hunting clothing prep: wash, scent treatment',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -30,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['clean'],
      sortOrder: 30,
      notes: 'Wash hunting clothes in scent-free detergent. Store in scent-free container. Treat boots.',
    },
    // ─── Opener-relative: -14 days ────────────────────────────────────────────
    {
      id: T.cameraReposition,
      templateId: TEMPLATE_ID,
      title: 'Camera repositioning: away from bedding pressure',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -14,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['reposition'],
      sortOrder: 31,
      notes: 'Move cameras away from bedding areas to reduce pressure. Focus on food source entries and travel corridors.',
    },
    // ─── Opener-relative: -7 days ─────────────────────────────────────────────
    {
      id: T.finalAccessRoute,
      templateId: TEMPLATE_ID,
      title: 'Final access route check: clear, quiet entry/exit',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -7,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['clear'],
      sortOrder: 32,
      notes: 'Walk all access routes. Clear noisy branches/leaves. Confirm quiet entry and exit paths for opening morning.',
    },
    // ─── Opener-relative: -1 day ──────────────────────────────────────────────
    {
      id: T.packBags,
      templateId: TEMPLATE_ID,
      title: 'Pack bags, confirm equipment list',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -1,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['pack'],
      sortOrder: 33,
      notes: 'Final gear check. Clothes, optics, safety gear, licenses, knives, drag rope, game bags, flashlights, snacks.',
    },
    // ─── Post-season: +30 and +45 days ───────────────────────────────────────
    {
      id: T.postSeasonCameraReview,
      templateId: TEMPLATE_ID,
      title: 'Post-season camera data review',
      description: '',
      category: TaskCategory.PostSeason,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 30,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['analyze', 'review'],
      sortOrder: 34,
      notes: 'Pull all cameras. Review full season data. Identify survivors, late-season movement patterns. Feeds next year planning.',
    },
    {
      id: T.postSeasonPlotEval,
      templateId: TEMPLATE_ID,
      title: 'Post-season plot evaluation: performance notes',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 30,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['evaluate', 'document'],
      sortOrder: 35,
      notes: 'Rate each food plot performance. Note which species attracted deer, which failed. Informs next year seed order.',
    },
    {
      id: T.equipmentStorage,
      templateId: TEMPLATE_ID,
      title: 'Equipment storage and maintenance',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 45,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['clean', 'store'],
      sortOrder: 36,
      notes: 'Clean firearms. Store in climate-controlled space. Service bow. Inventory and note replacements needed.',
    },
  ],
};
